import React, { useMemo } from "react";
import addDays from "date-fns/addDays";
import axios from "axios";
import moment from 'moment';
import { AlertColor, Link, TextField, useMediaQuery} from "@mui/material";
import { DataGridPro, GridCellParams, GridColumns, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { BaseControl, CheckboxControl, DatePickerControl, DropDownComboControl, DropDownComboOption, GenericFilter, InputBox } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { InvoiceHistoryFilter } from "../../../models/classes/InvoiceFilter";
import { CustomTablePagination } from "../../component/CustomPagination";
import { Invoice, InvoiceHistoryItem } from "./InvoiceAccessories";
import { InvoiceSingle, InvoiceSingleHandle } from "./InvoiceSingle";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { ManufacturerCombo } from "../manufacturers/ManufacturerAccessories";
import { renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { OpenMode } from "../../../common/Enums";
import { FormatNumber } from "../../../common/Formatter";

export function InvoiceHistory(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<InvoiceSingleHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

	const [manufacturers, setManufacturers] = React.useState<DropDownComboOption[]>([]);

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        endUser: 0,
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData() {
		setLoading(true);
		
        let filter = new InvoiceHistoryFilter({
			SearchString: searchData.searchText,
			CustomerID: searchData.customer % 100000,
			EndUserID: searchData.endUser,
			Reader: ""
		});

		axios.post<InvoiceHistoryItem[]>('Api/V1/Invoice/History', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				let rowId = 0;
				result.data.forEach(i => {
					i.id = rowId;
					rowId++;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.invoiceNo > b.invoiceNo) { return -1; }
						if (a.invoiceNo < b.invoiceNo) { return 1; }
					}
					return 0;					
				}))
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		let loadedManufacturers: DropDownComboOption[] = [];
		axios.get<ManufacturerCombo[]>('Api/V1/Manufacturer/Combo', {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.map((e) => loadedManufacturers.push({ name: e.name, value: e.manufacturerID.toString() }));
				setManufacturers(loadedManufacturers);
			},
				(error) => {
					console.log(error);
					setLoading(false);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				setLoading(false);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});

		let endUsersDropDownOption: DropDownComboOption[] = [];
		props.endUsers.map(e => endUsersDropDownOption.push({ name: e.endUserName, value: e.endUserId.toString() }));
		setControls([
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl,
			{
				key: 'endUser',
				title: 'End User',
				dropdownOptions: endUsersDropDownOption
			} as DropDownComboControl
		]);
		LoadData();
    }

    const columnWidth25 = useMemo(() => GetColumnWidth(25, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth35 = useMemo(() => GetColumnWidth(35, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth40 = useMemo(() => GetColumnWidth(40, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth50 = useMemo(() => GetColumnWidth(50, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth55 = useMemo(() => GetColumnWidth(55, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth60 = useMemo(() => GetColumnWidth(60, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth68 = useMemo(() => GetColumnWidth(68, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth70 = useMemo(() => GetColumnWidth(70, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth80 = useMemo(() => GetColumnWidth(80, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth100 = useMemo(() => GetColumnWidth(100, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    const columnWidth120 = useMemo(() => GetColumnWidth(120, tinyScreen, smallScreen), [tinyScreen, smallScreen]);
    function GetColumnWidth(width: number, isTiny: boolean, isSmall: boolean) {
		switch (width) {
			case 25:
			return isTiny ? 25 : (isSmall ? 30 : 35);
			case 35:
			return isTiny ? 35 : (isSmall ? 40 : 45);
			case 40:
			return isTiny ? 40 : (isSmall ? 45 : 55);
			case 50:
			return isTiny ? 50 : (isSmall ? 58 : 68);
			case 55:
			return isTiny ? 55 : (isSmall ? 70 : 80);
			case 60:
			return isTiny ? 60 : (isSmall ? 80 : 90);
			case 68:
			return isTiny ? 68 : (isSmall ? 85 : 100);
			case 70:
			return isTiny ? 70 : (isSmall ? 90 : 105);
			case 80:
			return isTiny ? 80 : (isSmall ? 100 : 110);
			case 100:
			return isTiny ? 100 : (isSmall ? 125 : 150);
			case 120:
			return isTiny ? 120 : (isSmall ? 155 : 190);
			default:
			return width;
		}
    }

	//Grid columns
	const columns: GridColumns = [
		{ 
			field: 'model', headerName: '型式', editable: true, 
			width: columnWidth120, minWidth: columnWidth120,
			type: 'string' 
		},
		{ field: 'serialNo', headerName: 'S/N', flex: 1.5, type: 'string' },
		{ field: 'createDate', headerName: 'Date', flex: 1.5, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
		{
			field: 'qty', headerName: '数量', disableColumnMenu: true, sortable: false,
			width: columnWidth50, minWidth: columnWidth50,
			type: 'number', align: 'right',
			cellClassName: style.NumberTextField
		},
		{ field: 'currency', headerName: 'CCY', flex: 1, type: 'string' },
		{
			field: 'euPrice', headerName: 'Buy @Price', disableColumnMenu: true, sortable: false,
			width: columnWidth70, minWidth: columnWidth70,
			type: 'number', editable: true, align: 'right',
			valueGetter: (param) => FormatNumber(param.row.euPrice, 2),
			cellClassName: style.NumberTextField
		},
		{ field: 'customerName', headerName: 'Customer', flex: 5, type: 'string' },
		{ field: 'endUser', headerName: 'End User', flex: 5, type: 'string' },
		{
			field: 'invoiceNo', headerName: 'Invoice No', flex: 2.2, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						OpenInvoice(params.row.invoiceNo);
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'staff', headerName: 'Sales', flex: 2, type: 'string' }
	];

	const [gridData, setGridData] = React.useState<InvoiceHistoryItem[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
			</GridToolbarContainer>
		);
	}

	function OpenInvoice(invoiceNo: string) {
        setLoading(true);
		axios.get<Invoice>('Api/V1/Invoice/' + invoiceNo.replace(/\//g, "%2F"), {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				result.data.createdBy=''; //createBy cannot be null when save
				result.data.status='ACT'; //status cannot be null when save
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data, false);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}
  
    function DoSearch(event: React.FormEvent<any>) {
		if (event)
        	event.preventDefault();
    
		LoadData();
    }

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Invoice History" />
			<GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="S/N / 商品CD / 型式"/>
			<div className={style.PageContent}>
				<DataGridPro
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					onRowDoubleClick={(params) => OpenInvoice(params.row.invoiceNo)}
				/>
			</div>

			<InvoiceSingle
				ref={singleModalRef}
				manufacturers={manufacturers}
				customers={props.customers} 
				endUsers={props.endUsers} 
				regions={props.regions}
				sales={props.sales} 
				deliveryMethods={props.deliveryMethods}
				paymentTerms={props.paymentTerms}
				currencies={props.currencies}
				onClose={(saved: boolean, invoice: Invoice) => {} } 
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

